import React from "react";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { Search, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";

export default function TransactionsListTab({
  transactions,
  txLoading,
  filters,
  setFilters,
  page,
  setPage,
  pageSize,
  totalTransactions,
  totalPages,
  onView,
  onClearFilters,
  t,
}) {
  const customDateRangeLabel = (filters.dateFrom || filters.dateTo)
    ? `${filters.dateFrom ? format(filters.dateFrom, "MMM d") : "Start"} - ${filters.dateTo ? format(filters.dateTo, "MMM d") : "End"}`
    : null;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-slate-100">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t ? t("searchIdRefMerchant") : "Search by ID or reference..."}
            value={filters.search}
            onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
            className="pl-10"
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(0); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder={t ? t("allStatus") : "All Status"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t ? t("allStatus") : "All Status"}</SelectItem>
            <SelectItem value="pending">{t ? t("pending") : "Pending"}</SelectItem>
            <SelectItem value="processing">{t ? t("processing") : "Processing"}</SelectItem>
            <SelectItem value="success">{t ? t("success") : "Success"}</SelectItem>
            <SelectItem value="declined">{t ? t("declined") : "Declined"}</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 border-slate-200">
              <CalendarIcon className="h-4 w-4 text-slate-400" />
              <span>{customDateRangeLabel || (t ? t("dateRange") : "Date Range")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={{ from: filters.dateFrom, to: filters.dateTo }}
              onSelect={(range) => {
                setFilters({ ...filters, dateFrom: range?.from || null, dateTo: range?.to || null });
                setPage(0);
              }}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
        {(filters.search || filters.status !== "all" || filters.dateFrom) && (
          <Button variant="ghost" onClick={onClearFilters} className="text-slate-500 underline text-xs">
            {t ? t("clear") : "Clear"}
          </Button>
        )}
      </div>

      <AdminTransactionTable
        transactions={transactions}
        isLoading={txLoading}
        onView={onView}
        showMerchant={false}
        showReseller={false}
        mixedTypes={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-500">
            {t ? t("showing") : "Showing"} {page * pageSize + 1} {t ? t("to") : "to"} {Math.min((page + 1) * pageSize, totalTransactions)} {t ? t("of") : "of"} {totalTransactions.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">
              {t ? t("page") : "Page"} {page + 1} {t ? t("of") : "of"} {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
